import { useQuery } from '@tanstack/react-query';
import { TrendingDown, TrendingUp, Users } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { CardShell } from '../cards/card-shell';

/**
 * KPI principal: total de contatos do escritorio + crescimento vs mes
 * anterior. Cor do chip muda conforme o sinal do crescimento.
 */
export function SummaryTotalWidget() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['clients', 'stats', 'summary'],
    queryFn: () => apiClient.clientsStatsSummary(),
    staleTime: 1000 * 60,
  });

  const total = data?.totalContacts ?? 0;
  const growth = data?.growthVsLastMonth ?? 0;

  // Aproximacao do "mes anterior" exibida em texto: total / (1 + growth/100).
  // Quando growth=0, mantemos igual ao total.
  const previousMonth =
    growth === 0 || total === 0
      ? 0
      : Math.max(0, Math.round(total / (1 + growth / 100)) - total);

  const positive = growth >= 0;

  return (
    <CardShell title="Contatos cadastrados" icon={Users}>
      <div className="flex w-full flex-col items-start gap-2">
        {isLoading ? (
          <div className="flex w-full flex-col gap-2">
            <div className="h-10 w-20 animate-pulse rounded bg-[var(--surface-muted)]" />
            <div className="h-3 w-32 animate-pulse rounded bg-[var(--surface-muted)]" />
          </div>
        ) : isError ? (
          <p className="text-xs text-[var(--fg-muted)]">
            Não foi possível carregar.
          </p>
        ) : (
          <>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-semibold tracking-tight text-[var(--fg)]">
                {total}
              </span>
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                  positive
                    ? 'bg-emerald-500/15 text-emerald-500'
                    : 'bg-red-500/15 text-red-500',
                )}
              >
                {positive ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                {positive ? '+' : ''}
                {growth}%
              </span>
            </div>
            <p className="text-[11px] text-[var(--fg-muted)]">
              {previousMonth !== 0
                ? `No mês anterior: ${previousMonth > 0 ? '+' : ''}${previousMonth}`
                : 'Comparação com mês anterior'}
            </p>
          </>
        )}
      </div>
    </CardShell>
  );
}
